import { useMemo, useState } from 'react';
import { Alert } from '@aigovops/design-system';
import { SampleShell, DispositionChip } from './SampleShell';
import {
  BUILDING_ROWS,
  BUILDING_SUMMARY,
  BUILDING_DISPOSITION_LABELS,
  CATEGORY_LABELS,
  EU_AI_ACT_LABELS,
  type BuildingDisposition,
  type BuildingSystemRow,
} from '../../data/samples/building-register';

/**
 * Public sample of the Building AI register — GAP-2 in docs/v4/link-gaps-plan.md.
 * Teal accent (Building lane). Read-only, no auth, static seed data.
 */

const DISPOSITIONS: Array<{ key: BuildingDisposition; tone: 'systemic' | 'pending' | 'awaiting' | 'outside' }> = [
  { key: 'governed', tone: 'systemic' },
  { key: 'pending_review', tone: 'pending' },
  { key: 'awaiting_classification', tone: 'awaiting' },
  { key: 'outside_oversight_scope', tone: 'outside' },
];

const toneForDisposition = (d: BuildingDisposition): 'systemic' | 'pending' | 'awaiting' | 'outside' | 'retired' => {
  switch (d) {
    case 'governed': return 'systemic';
    case 'pending_review': return 'pending';
    case 'awaiting_classification': return 'awaiting';
    case 'outside_oversight_scope': return 'outside';
    case 'retired': return 'retired';
  }
};

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toISOString().slice(0, 10);
};

const daysBetween = (iso: string | null): number | null => {
  if (!iso) return null;
  const diff = Date.parse(iso) - Date.now();
  return Math.round(diff / (1000 * 60 * 60 * 24));
};

export function BuildingAiRegisterSample() {
  const [filter, setFilter] = useState<BuildingDisposition | 'all'>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filtered = useMemo(
    () => (filter === 'all' ? BUILDING_ROWS : BUILDING_ROWS.filter((r) => r.disposition === filter)),
    [filter],
  );

  const selected = BUILDING_ROWS.find((r) => r.id === selectedId) ?? null;

  return (
    <SampleShell
      accent="building"
      eyebrow="SAMPLE · BUILDING AI REGISTER"
      title="See which of your AI systems are governed — so your buyers stop sending 300-row questionnaires."
      subtitle="Every AI system you deploy or ship — internal productivity tools, AI features in your product, SaaS you're the deployer of. One register. Attest once; render on demand."
      laneHref="/building"
      laneLabel="Building overview"
      crossLane={{
        href: '/buying/samples/supplier-ai-register',
        label: 'Or see the buyer\'s view — the AI you procure',
      }}
    >
      {/* Summary tiles */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-6)',
        }}
      >
        <SummaryTile
          label="Systems in register"
          value={BUILDING_SUMMARY.totalSystems}
          caption={`${BUILDING_SUMMARY.shippedToCustomers} shipped · ${BUILDING_SUMMARY.internalOnly} internal`}
        />
        <SummaryTile
          label="Under systemic oversight"
          value={BUILDING_SUMMARY.governed}
          caption="Evidence trail complete, owner signed"
          accent
        />
        <SummaryTile
          label="Questionnaire-ready"
          value={BUILDING_SUMMARY.questionnaireReady}
          caption="Render a cited response in a day, not six weeks"
        />
        <SummaryTile
          label="EU AI Act high-risk"
          value={BUILDING_SUMMARY.highRiskEuAiAct}
          caption="Full Article-required documentation"
        />
      </div>

      {/* Deployer-role callout — the seam Anika + Elena flagged */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <Alert
          variant="info"
          title="Includes SaaS-embedded AI you deploy — Copilot, Einstein, and equivalents."
        >
          If your organization is the deployer under EU AI Act Article 3(4) — your data, your
          deployment decision — the system belongs in this register regardless of who built the
          model. Two entries in this sample show that pattern.
        </Alert>
      </div>

      {/* Disposition filter chips */}
      <div
        role="tablist"
        aria-label="Filter by disposition"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-4)',
        }}
      >
        <FilterChip label="All" count={BUILDING_ROWS.length} active={filter === 'all'} onClick={() => setFilter('all')} />
        {DISPOSITIONS.map((d) => {
          const count = BUILDING_ROWS.filter((r) => r.disposition === d.key).length;
          return (
            <FilterChip
              key={d.key}
              label={BUILDING_DISPOSITION_LABELS[d.key]}
              count={count}
              active={filter === d.key}
              onClick={() => setFilter(d.key)}
            />
          );
        })}
      </div>

      {/* Table */}
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          overflow: 'hidden',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-xs)',
          }}
        >
          <thead>
            <tr style={{ background: 'var(--surface-elevated)', color: 'var(--text-dim)' }}>
              <TableHead>System</TableHead>
              <TableHead>Disposition</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>EU AI Act</TableHead>
              <TableHead>Frameworks</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Next review</TableHead>
              <TableHead>Questionnaire</TableHead>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <BuildingTableRow
                key={row.id}
                row={row}
                selected={selectedId === row.id}
                onClick={() => setSelectedId(row.id === selectedId ? null : row.id)}
              />
            ))}
          </tbody>
        </table>
        {filtered.length === 0 ? (
          <div
            style={{
              padding: 'var(--space-6)',
              textAlign: 'center',
              color: 'var(--text-muted)',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            No systems match this filter.
          </div>
        ) : null}
      </div>

      {/* Detail drawer */}
      {selected ? <BuildingDetail row={selected} onClose={() => setSelectedId(null)} /> : null}
    </SampleShell>
  );
}

function SummaryTile({
  label,
  value,
  caption,
  accent,
}: {
  label: string;
  value: number;
  caption: string;
  accent?: boolean;
}) {
  const borderColor = accent ? 'var(--building)' : 'var(--border)';
  return (
    <div
      style={{
        padding: 'var(--space-4)',
        border: `1px solid ${borderColor}`,
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--font-size-3xs)',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: 'var(--text-dim)',
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-ui)',
          fontSize: 'clamp(1.75rem, 3vw, 2.25rem)',
          fontWeight: 700,
          color: 'var(--text)',
          lineHeight: 1,
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
        {caption}
      </div>
    </div>
  );
}

function FilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2) var(--space-3)',
        border: `1px solid ${active ? 'var(--building)' : 'var(--border-strong)'}`,
        borderRadius: '999px',
        background: active ? 'var(--building-soft)' : 'var(--surface)',
        color: active ? 'var(--building)' : 'var(--text-muted)',
        fontFamily: 'var(--font-ui)',
        fontSize: 'var(--font-size-2xs)',
        fontWeight: active ? 700 : 500,
        cursor: 'pointer',
      }}
    >
      {label}
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--font-size-3xs)',
          padding: '0 var(--space-1)',
          borderRadius: '999px',
          background: active ? 'var(--building)' : 'var(--surface-elevated)',
          color: active ? 'var(--text-inverse)' : 'var(--text-dim)',
        }}
      >
        {count}
      </span>
    </button>
  );
}

function TableHead({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: 'var(--space-3)',
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--font-size-3xs)',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontWeight: 500,
        borderBottom: '1px solid var(--border)',
      }}
    >
      {children}
    </th>
  );
}

function BuildingTableRow({
  row,
  selected,
  onClick,
}: {
  row: BuildingSystemRow;
  selected: boolean;
  onClick: () => void;
}) {
  const nextDueDays = daysBetween(row.nextEvaluationDue);
  const dueLabel =
    nextDueDays === null
      ? '—'
      : nextDueDays < 0
        ? `${Math.abs(nextDueDays)}d overdue`
        : `${nextDueDays}d`;
  const dueColor = nextDueDays !== null && nextDueDays < 0 ? 'var(--band-red)' : 'var(--text-muted)';

  return (
    <tr
      onClick={onClick}
      style={{
        borderTop: '1px solid var(--border)',
        cursor: 'pointer',
        background: selected ? 'var(--building-soft)' : 'transparent',
      }}
    >
      <td style={{ padding: 'var(--space-3)' }}>
        <div>
          <div style={{ fontWeight: 600, color: 'var(--text)' }}>{row.systemName}</div>
          <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
            {CATEGORY_LABELS[row.category]}
          </div>
        </div>
      </td>
      <td style={{ padding: 'var(--space-3)' }}>
        <DispositionChip
          label={BUILDING_DISPOSITION_LABELS[row.disposition]}
          tone={toneForDisposition(row.disposition)}
        />
      </td>
      <td style={{ padding: 'var(--space-3)' }}>
        <span
          style={{
            padding: '2px var(--space-2)',
            borderRadius: '999px',
            background: row.scope === 'shipped' ? 'var(--building-soft)' : 'var(--surface-elevated)',
            color: row.scope === 'shipped' ? 'var(--building)' : 'var(--text-muted)',
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--font-size-3xs)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontWeight: 700,
          }}
        >
          {row.scope}
        </span>
      </td>
      <td style={{ padding: 'var(--space-3)' }}>
        <EuAiActBadge tier={row.euAiActTier} />
      </td>
      <td style={{ padding: 'var(--space-3)', color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
        {row.frameworkTags.slice(0, 3).join(' · ')}
        {row.frameworkTags.length > 3 ? ` +${row.frameworkTags.length - 3}` : ''}
      </td>
      <td style={{ padding: 'var(--space-3)' }}>
        <div style={{ color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>
          {row.owner.displayName}
        </div>
        <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
          {row.owner.role}
        </div>
      </td>
      <td style={{ padding: 'var(--space-3)', color: dueColor, fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-3xs)' }}>
        {dueLabel}
      </td>
      <td style={{ padding: 'var(--space-3)' }}>
        {row.questionnaireReady ? (
          <span
            style={{
              padding: '2px var(--space-2)',
              borderRadius: 'var(--radius-card)',
              background: 'var(--band-green-bg)',
              color: 'var(--band-green)',
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--font-size-3xs)',
              fontWeight: 700,
            }}
          >
            READY
          </span>
        ) : (
          <span
            style={{
              padding: '2px var(--space-2)',
              borderRadius: 'var(--radius-card)',
              background: 'var(--surface-elevated)',
              color: 'var(--text-dim)',
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--font-size-3xs)',
              fontWeight: 500,
            }}
          >
            —
          </span>
        )}
      </td>
    </tr>
  );
}

function EuAiActBadge({ tier }: { tier: BuildingSystemRow['euAiActTier'] }) {
  const style: Record<BuildingSystemRow['euAiActTier'], { bg: string; fg: string }> = {
    unacceptable: { bg: 'var(--band-red-bg)', fg: 'var(--band-red)' },
    high_risk: { bg: 'var(--band-red-bg)', fg: 'var(--band-red)' },
    limited_risk: { bg: 'var(--band-amber-bg)', fg: 'var(--band-amber)' },
    minimal_risk: { bg: 'var(--surface-elevated)', fg: 'var(--text-muted)' },
    gpai: { bg: 'var(--band-blue-bg)', fg: 'var(--band-blue)' },
  };
  const s = style[tier];
  return (
    <span
      style={{
        padding: '2px var(--space-2)',
        borderRadius: '999px',
        background: s.bg,
        color: s.fg,
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--font-size-3xs)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      {EU_AI_ACT_LABELS[tier]}
    </span>
  );
}

function BuildingDetail({ row, onClose }: { row: BuildingSystemRow; onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-label={`Detail for ${row.systemName}`}
      style={{
        marginTop: 'var(--space-4)',
        border: `1px solid var(--building)`,
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-6)',
        display: 'grid',
        gap: 'var(--space-4)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--font-size-3xs)',
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: 'var(--building)',
              fontWeight: 700,
            }}
          >
            SYSTEM · {CATEGORY_LABELS[row.category].toUpperCase()}
          </div>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg)', color: 'var(--text)' }}>
            {row.systemName}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: 'none',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-card)',
            color: 'var(--text-muted)',
            padding: 'var(--space-1) var(--space-3)',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-xs)',
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </div>

      <p
        style={{
          margin: 0,
          fontFamily: 'var(--font-ui)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
          lineHeight: 'var(--line-height-body)',
        }}
      >
        {row.narrative}
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))',
          gap: 'var(--space-3)',
        }}
      >
        <MiniStat label="Scope" value={row.scope === 'shipped' ? 'Shipped to customers' : 'Internal only'} />
        <MiniStat label="EU AI Act tier" value={EU_AI_ACT_LABELS[row.euAiActTier]} />
        <MiniStat label="Disposition" value={BUILDING_DISPOSITION_LABELS[row.disposition]} />
        <MiniStat label="Named owner" value={`${row.owner.displayName} · ${row.owner.role}`} />
        <MiniStat label="Last evaluated" value={fmtDate(row.lastEvaluated)} />
        <MiniStat label="Next review due" value={fmtDate(row.nextEvaluationDue)} />
      </div>

      {/* Evidence checklist */}
      <div
        style={{
          padding: 'var(--space-3)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface-elevated)',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--font-size-3xs)',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: 'var(--text-dim)',
            marginBottom: 'var(--space-2)',
            fontWeight: 600,
          }}
        >
          Evidence trail
        </div>
        <div style={{ display: 'grid', gap: 'var(--space-2)', fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-xs)' }}>
          <EvidenceRow ok={row.hasEvaluationRecord} label="Evaluation record" />
          <EvidenceRow ok={row.hasDataLineage} label="Data lineage documentation" />
          <EvidenceRow ok={row.attestedSigner !== null} label={row.attestedSigner ? `Signed by ${row.attestedSigner.displayName}, ${row.attestedSigner.role}` : 'No signer on file'} />
          <EvidenceRow ok={row.questionnaireReady} label={row.questionnaireReady ? 'Ready to render a prospect questionnaire on demand' : 'Not questionnaire-ready — evidence gaps above'} />
        </div>
      </div>

      {/* Framework mappings */}
      <div>
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--font-size-3xs)',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: 'var(--text-dim)',
            marginBottom: 'var(--space-2)',
            fontWeight: 600,
          }}
        >
          Frameworks that apply
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          {row.frameworkTags.map((tag) => (
            <span
              key={tag}
              style={{
                padding: '2px var(--space-2)',
                border: '1px solid var(--border-strong)',
                borderRadius: '999px',
                background: 'var(--surface-elevated)',
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--font-size-3xs)',
                color: 'var(--text-muted)',
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function EvidenceRow({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
      <span
        aria-hidden="true"
        style={{
          fontFamily: 'var(--font-mono)',
          color: ok ? 'var(--band-green)' : 'var(--text-dim)',
          fontWeight: 700,
        }}
      >
        {ok ? '✓' : '○'}
      </span>
      <span style={{ color: ok ? 'var(--text)' : 'var(--text-muted)' }}>{label}</span>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'grid', gap: '2px' }}>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--font-size-3xs)',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--text-dim)',
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-ui)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text)',
          fontWeight: 500,
        }}
      >
        {value}
      </div>
    </div>
  );
}
